import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, request } from '../api/client';

interface Compra {
  id: string;
  produto: string;
  status: string;
  valorCentavos: number;
  kiwifyOrderId: string;
  tokenValidacao: string | null;
  compradaEm: string;
  reembolsadaEm: string | null;
  cliente: {
    id: string;
    primeiroNome: string;
    nomeCompleto: string | null;
    telefone: string;
    email: string | null;
  };
  conversaValidada: boolean;
  conversaPausada: boolean;
  ultimaMensagemEm: string | null;
  ultimoEmail: {
    status: string;
    sentAt: string | null;
    deliveredAt: string | null;
    openedAt: string | null;
    clickedAt: string | null;
    bouncedAt: string | null;
    errorMessage: string | null;
  } | null;
}

const PRODUTO_FILTERS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Todos' },
  { value: 'front', label: 'Front (Semente)' },
  { value: 'upsell1', label: 'Upsell (Mapa)' },
];

const STATUS_FILTERS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Todos status' },
  { value: 'aprovada', label: 'Aprovada' },
  { value: 'reembolsada', label: 'Reembolsada' },
  { value: 'estornada', label: 'Estornada' },
  { value: 'cancelada', label: 'Cancelada' },
];

const EMAIL_STATUS_COLOR: Record<string, string> = {
  pending: 'bg-sara-bg-muted text-sara-text-muted',
  sent: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  delivered: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  opened: 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300',
  clicked: 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300',
  bounced: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  complained: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

export function ComprasPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [produto, setProduto] = useState('');
  const [status, setStatus] = useState('');

  const { data, isLoading, isError, refetch, isFetching } = useQuery<{
    items: Compra[];
    total: number;
  }>({
    queryKey: ['compras', produto, status],
    queryFn: () => {
      const params = new URLSearchParams({ limit: '200' });
      if (produto) params.set('produto', produto);
      if (status) params.set('status', status);
      return request<{ items: Compra[]; total: number }>(`/admin/compras?${params}`);
    },
    refetchInterval: 60_000,
  });

  const reenviar = useMutation({
    mutationFn: (id: string) =>
      request<{ ok: true; envioId: string | null; toEmail: string }>(
        `/admin/compras/${id}/reenviar-email`,
        { method: 'POST', body: {} },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compras'] });
    },
  });

  const syncKiwify = useMutation({
    mutationFn: () =>
      request<{
        ok: true;
        fetched: number;
        created: number;
        skipped: number;
        ignored: number;
        errors: number;
      }>('/admin/compras/sync-kiwify', { method: 'POST', body: { lookbackDays: 90 } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compras'] });
    },
  });

  const sendBulk = useMutation({
    mutationFn: () =>
      request<{ ok: true; sent: number; skipped: number; errors: number }>(
        '/admin/compras/send-codes-bulk',
        { method: 'POST', body: {} },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compras'] });
    },
  });

  return (
    <section aria-labelledby="compras-heading" className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 id="compras-heading" className="h1">
            Compras Kiwify
          </h1>
          <p className="muted text-sm mt-1">
            Lista de quem comprou, status do email com token, e validação na Sara. Atualiza a cada
            60s.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => syncKiwify.mutate()}
            className="btn-ghost text-sm"
            disabled={syncKiwify.isPending}
            aria-busy={syncKiwify.isPending}
            title="Busca os últimos 90 dias de vendas na Kiwify Public API e importa as que ainda não estão no DB."
          >
            {syncKiwify.isPending ? 'Sincronizando…' : '⟳ Sincronizar Kiwify (90d)'}
          </button>
          <button
            type="button"
            onClick={() => sendBulk.mutate()}
            className="btn-ghost text-sm"
            disabled={sendBulk.isPending}
            aria-busy={sendBulk.isPending}
            title="Manda email com token pra todas as compras aprovadas que ainda não receberam."
          >
            {sendBulk.isPending ? 'Enviando…' : '✉ Enviar códigos pra todos sem email'}
          </button>
          <button onClick={() => refetch()} className="btn-ghost text-sm" disabled={isFetching}>
            {isFetching ? 'Atualizando…' : '↻ Atualizar'}
          </button>
        </div>
      </header>

      {syncKiwify.isSuccess && syncKiwify.data && (
        <div role="status" className="card text-sm">
          ✓ Sincronização: <strong>{syncKiwify.data.created}</strong> criadas,{' '}
          <strong>{syncKiwify.data.skipped}</strong> já existiam,{' '}
          <strong>{syncKiwify.data.ignored}</strong> fora do catálogo,{' '}
          <strong>{syncKiwify.data.errors}</strong> erros ({syncKiwify.data.fetched} vendas
          verificadas).
        </div>
      )}
      {syncKiwify.isError && (
        <p role="alert" className="card text-sara-danger text-sm">
          {syncKiwify.error instanceof ApiError
            ? syncKiwify.error.message
            : 'Erro sincronizando Kiwify'}
        </p>
      )}
      {sendBulk.isSuccess && sendBulk.data && (
        <div role="status" className="card text-sm">
          ✓ Bulk send: <strong>{sendBulk.data.sent}</strong> emails enviados,{' '}
          <strong>{sendBulk.data.skipped}</strong> já tinham,{' '}
          <strong>{sendBulk.data.errors}</strong> erros.
        </div>
      )}
      {sendBulk.isError && (
        <p role="alert" className="card text-sara-danger text-sm">
          {sendBulk.error instanceof ApiError ? sendBulk.error.message : 'Erro no envio em massa'}
        </p>
      )}

      <div className="card space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap items-center gap-1.5">
            {PRODUTO_FILTERS.map((f) => (
              <button
                key={f.value || 'p-all'}
                onClick={() => setProduto(f.value)}
                className={`text-xs px-3 py-1.5 rounded-md border ${
                  produto === f.value
                    ? 'border-sara-accent bg-sara-accent/10 text-sara-accent'
                    : 'border-sara-border text-sara-text-muted hover:text-sara-text'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value || 's-all'}
                onClick={() => setStatus(f.value)}
                className={`text-xs px-3 py-1.5 rounded-md border ${
                  status === f.value
                    ? 'border-sara-accent bg-sara-accent/10 text-sara-accent'
                    : 'border-sara-border text-sara-text-muted hover:text-sara-text'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          {data && (
            <span className="text-xs text-sara-text-subtle ml-auto">{data.total} no total</span>
          )}
        </div>

        {isLoading && <p className="text-sara-text-muted text-sm">carregando…</p>}
        {isError && (
          <p role="alert" className="text-sara-danger text-sm">
            erro carregando compras
          </p>
        )}

        {data && data.items.length === 0 && (
          <p className="text-sara-text-muted text-sm py-8 text-center">
            Nenhuma compra com esses filtros.
          </p>
        )}

        {data && data.items.length > 0 && (
          <div className="overflow-x-auto -mx-3">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-sara-text-subtle border-b border-sara-border">
                <tr>
                  <th className="px-3 py-2">Cliente</th>
                  <th className="px-3 py-2">Produto</th>
                  <th className="px-3 py-2">Token</th>
                  <th className="px-3 py-2">Validou Sara?</th>
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">Última msg</th>
                  <th className="px-3 py-2">Comprou</th>
                  <th className="px-3 py-2">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-sara-border">
                {data.items.map((c) => (
                  <tr key={c.id} className="hover:bg-sara-bg-muted/40">
                    <td className="px-3 py-2 align-top">
                      <div className="font-medium text-sara-text">
                        {c.cliente.nomeCompleto ?? c.cliente.primeiroNome}
                      </div>
                      <div className="text-xs text-sara-text-subtle">{c.cliente.telefone}</div>
                      {c.cliente.email && (
                        <div className="text-xs text-sara-text-subtle">{c.cliente.email}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div className="text-sara-text">{c.produto}</div>
                      <div className="text-xs text-sara-text-subtle">
                        R$ {(c.valorCentavos / 100).toFixed(2)}
                      </div>
                      <span
                        className={`inline-block text-[10px] px-1.5 py-0.5 rounded mt-1 ${
                          c.status === 'aprovada'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                            : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                        }`}
                      >
                        {c.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 align-top">
                      {c.tokenValidacao ? (
                        <code className="text-xs bg-sara-bg-muted px-1.5 py-0.5 rounded">
                          {c.tokenValidacao}
                        </code>
                      ) : (
                        <span className="text-sara-text-subtle">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top text-xs">
                      {c.conversaValidada ? (
                        <span className="text-sara-success">✓ validou</span>
                      ) : (
                        <span className="text-sara-text-subtle">não usou</span>
                      )}
                      {c.conversaPausada && (
                        <div className="text-orange-600 dark:text-orange-400">⏸ pausada</div>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top">
                      {c.ultimoEmail ? (
                        <div className="space-y-1">
                          <span
                            className={`inline-block text-xs px-2 py-0.5 rounded ${
                              EMAIL_STATUS_COLOR[c.ultimoEmail.status] ??
                              'bg-sara-bg-muted text-sara-text-muted'
                            }`}
                          >
                            {c.ultimoEmail.status}
                          </span>
                          {c.ultimoEmail.openedAt && (
                            <div className="text-[10px] text-violet-600 dark:text-violet-400">
                              👁 aberto {formatRelative(c.ultimoEmail.openedAt)}
                            </div>
                          )}
                          {c.ultimoEmail.clickedAt && (
                            <div className="text-[10px] text-pink-600 dark:text-pink-400">
                              🖱 clicou {formatRelative(c.ultimoEmail.clickedAt)}
                            </div>
                          )}
                          {c.ultimoEmail.errorMessage && (
                            <div
                              className="text-[10px] text-sara-danger max-w-[180px] truncate"
                              title={c.ultimoEmail.errorMessage}
                            >
                              {c.ultimoEmail.errorMessage}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-sara-text-subtle text-xs">não enviado</span>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top text-xs text-sara-text-muted">
                      {c.ultimaMensagemEm ? formatRelative(c.ultimaMensagemEm) : '—'}
                    </td>
                    <td className="px-3 py-2 align-top text-xs text-sara-text-muted">
                      {formatRelative(c.compradaEm)}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <button
                        onClick={() => reenviar.mutate(c.id)}
                        disabled={reenviar.isPending || !c.cliente.email || !c.tokenValidacao}
                        className="btn-ghost text-xs"
                        title={
                          !c.cliente.email
                            ? 'Cliente sem email cadastrado'
                            : !c.tokenValidacao
                              ? 'Compra sem token'
                              : 'Reenviar email com o token'
                        }
                      >
                        {reenviar.isPending && reenviar.variables === c.id
                          ? 'Reenviando…'
                          : '✉ Reenviar'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {reenviar.isError && (
          <p role="alert" className="text-sara-danger text-xs">
            {reenviar.error instanceof ApiError ? reenviar.error.message : 'erro reenviando'}
          </p>
        )}
        {reenviar.isSuccess && (
          <p role="status" className="text-sara-success text-xs">
            ✓ email reenviado pra {reenviar.data.toEmail}
          </p>
        )}
      </div>
    </section>
  );
}

function formatRelative(iso: string): string {
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const min = Math.round(diffMs / 60_000);
  if (min < 1) return 'agora';
  if (min < 60) return `${min}min`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h}h`;
  const days = Math.round(h / 24);
  if (days < 30) return `${days}d`;
  return d.toLocaleDateString('pt-BR');
}
